import { MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import TablerIconMessage from '~icons/tabler/message';
import TablerIconRobot from '~icons/tabler/robot';
import ReleaseChip, { type ReleaseStage } from '#/components/Chips/ReleaseChip';

export interface ChatAssistantDropdownProps {
  disabled?: boolean;
}

function ChatAssistantDropdown({ disabled }: ChatAssistantDropdownProps) {
  return (
    <TextField
      select
      size='xsmall'
      className='min-w-[100px]'
      disabled={disabled}
      slotProps={{
        select: {
          displayEmpty: true,
          renderValue(value) {
            const option = findOption(value);
            return (
              <Stack direction='row' spacing={1} alignItems='center'>
                {option.icon}
                <span>{option.label}</span>
              </Stack>
            );
          },
        },
      }}
    >
      {options.map((option) => (
        <Tooltip
          key={option.value}
          title={option.description ?? undefined}
          placement='left'
          arrow
        >
          <span>
            <MenuItem
              value={option.value}
              disabled={option.status === 'soon'}
              disableGutters
              dense
            >
              <Stack
                direction='row'
                spacing={1}
                alignItems='center'
                className='px-2'
              >
                {option.icon}
                <span>{option.label}</span>
                {option.status === 'soon' && (
                  <ReleaseChip stage='soon' size='xsmall' shape='rounded' />
                )}
              </Stack>
            </MenuItem>
          </span>
        </Tooltip>
      ))}
    </TextField>
  );
}

export default ChatAssistantDropdown;

/*
|------------------
| Internals
|------------------
*/

function findOption(value: unknown) {
  return options.find((o) => o.value === value) ?? options[0];
}

interface ChatAssistantOption {
  icon: React.ReactNode;
  label: string;
  value: string;
  description?: string;
  status: ReleaseStage | 'soon';
}

const options = [
  {
    icon: <TablerIconMessage />,
    label: 'Ask',
    value: 'ask',
    description:
      'Ask questions and get answers about your docs, code, and more',
    status: 'stable',
  },
  {
    icon: <TablerIconRobot />,
    label: 'Agent',
    value: 'agent',
    description:
      'Plan, search, and execute tasks, such as creating Linear issues, writing docs, and more',
    status: 'soon',
  },
] satisfies ChatAssistantOption[];
